import {
  FacebookEmailTakenWarning,
  UnauthenticatedWarning,
} from 'app/components/auth/AuthenticationWarnings';
import AuthPageContent from 'app/components/auth/AuthPageContent';
import { LoginConsumer, LoginLoadingState, LoginProvider } from 'app/components/auth/LoginContext';
import LoginForm from 'app/components/auth/LoginForm';
import { getDestinationRouteForUser } from 'app/components/auth/UserSensitiveLink';
import { useQuery } from 'app/components/Contexts';
import Copyright from 'app/components/footer/Copyright';
import Header, { HeaderVariant } from 'app/components/header/Header';
import MarginGroup from 'app/components/layout/MarginGroup';
import Page, { PAGE_WIDE_STYLE } from 'app/components/layout/Page';
import { useI18n } from 'app/components/localization';
import NotFound from 'app/components/pages/NotFound';
import RedirectingPage from 'app/components/pages/RedirectingPage';
import { useHistory } from 'app/components/router/History';
import { IFrameContextProvider } from 'app/components/utilities/IFrameContext';
import appConfig from 'app/config/client';
import { RunningIn } from 'app/config/types/DeployInfo';
import { QueryParameter } from 'app/constants/QueryParameter';
import { TeacherRecruitmentSignUpRoute } from 'app/constants/Routes';
import User from 'app/constants/schemas/User';
import useRequireAuthentication from 'app/containers/auth/useRequireAuthentication';
import SignupForm from 'app/containers/auth/SignupForm';
import { sessionRequest } from 'app/models/auth/actions';
import { isTeacher } from 'app/models/auth/selectors';
import { History } from 'app/services/history';
import { LoginIntent } from 'app/services/oauth/constants';
import { getLatestTeacherApplicationForUser } from 'app/utils/api';
import { useDispatch } from 'react-redux';
import { getWithDefault } from 'app/utils/fpTs';
import { isUrlOffAppSite } from 'app/utils/isUrlOffAppSite';
import { parseBoolean } from 'app/utils/parse';
import React, { useCallback } from 'react';
import { AppDispatch } from 'redux';
import { BrandContextValue, useBrand } from '../OrganizationContext';
import { isTeacherSite } from 'app/constants/TeacherSite';
import { StringifiableRecord } from 'query-string';

export const LoginFooter = () => (
  <div>
    <Copyright />
  </div>
);

export interface IProps {
  intent: LoginIntent;
}

export interface IAuthQuery extends StringifiableRecord {
  [QueryParameter.ShowAuth]?: string;
  redirectTo?: string;
  restricted?: string;
  automatic?: string;
  iframe?: string;
  dmm_env?: string;
}

export const redirectAuthenticatedUserHelper = async ({
  user,
  redirectTo,
  history,
  brand,
  dispatch,
  wasAuthenticated = false,
  useLocalRouting = false,
}: {
  user: User;
  redirectTo: string;
  history: History;
  brand: BrandContextValue;
  dispatch: AppDispatch;
  wasAuthenticated?: boolean;
  useLocalRouting?: boolean;
}) => {
  const defaultDomainLocation = brand.defaultDomain.envUri;
  // Redirect to the pending application page if the user has a teacher application in progress and just
  // logged in (was not authenticated before). Otherwise, prefer redirectTo when provided, unless it is offsite.
  const route =
    (!wasAuthenticated && isTeacherSite(history.location.hostname) && await hasTeacherApplication(user, dispatch))
    ? TeacherRecruitmentSignUpRoute.getPathname()
    : (redirectTo && !isUrlOffAppSite(redirectTo))
    ? redirectTo
    : getDestinationRouteForUser(user, brand, defaultDomainLocation, useLocalRouting);

  history.smartReplace(route);
};

export const hasTeacherApplication = async (user: User, dispatch: AppDispatch) => {
  if (!isTeacher(user)) {
    const userId = user.id;
    const result = await dispatch(sessionRequest(getLatestTeacherApplicationForUser({ userId })));
    return Boolean(result && result.data && result.data[0]);
  }
  return false;
};

export const Auth: React.FunctionComponent<IProps> = ({ intent }) => {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const query = useQuery();
  const history = useHistory();
  const brand = useBrand();

  const redirectAuthenticatedUser = useCallback(async (user: User, wasAuthenticated: boolean) => {
    redirectAuthenticatedUserHelper({
      user,
      redirectTo: query.redirectTo,
      history,
      brand,
      dispatch,
      wasAuthenticated,
      useLocalRouting: appConfig.deployInfo.runningIn === RunningIn.Local,
    });
  }, [query.redirectTo, history, brand, dispatch]);

  const {
    restricted,
    automatic,
    iframe,
  } = query;

  const isRestricted = getWithDefault(parseBoolean(restricted), false);
  const forceAutoLogin = getWithDefault(parseBoolean(automatic), false);
  const iFrame = getWithDefault(parseBoolean(iframe), false);

  const { isAuthenticated } = useRequireAuthentication(redirectAuthenticatedUser);

  return (
    // The LoginProvider will not render its children when completing an oauth login.
    // It must be the top of this hierarchy to prevent spurious redirects when completing oauth.
    <LoginProvider forceAutoLogin={forceAutoLogin} intent={intent}>
      <LoginConsumer>{({ emailTaken, allowSignup, loadingState }) => {
        const form = (intent === LoginIntent.Register) && !emailTaken
                      ? <SignupForm />
                      : <LoginForm />;

        const title = intent === LoginIntent.Register
                      ? t('auth.signup-form.title', { brand: brand.name })
                      : t('page.login.title', { brand: brand.name });

        if (loadingState === LoginLoadingState.LOADING_INITIALIZING) {
          // Don't render anything while doing dmm_check, since we might just redirect
          // afterward anyway.
          return null;
        } else if (isAuthenticated || loadingState === LoginLoadingState.LOADING_AUTO_LOGIN) {
          return <RedirectingPage />;
        } else if (intent === LoginIntent.Register && !allowSignup) {
          return <NotFound />;
        } else if (iFrame) {
          return (
            <IFrameContextProvider value={{ isIFrame: true }}>
              {form}
            </IFrameContextProvider>
          );
        } else {
          return (
            <Page
              footer={<LoginFooter />}
              title={title}
              variant={PAGE_WIDE_STYLE}
              header={<Header
                variant={HeaderVariant.NoLogin}
              />}
            >
              <AuthPageContent>
                <MarginGroup margin="28px">
                  {isRestricted && <UnauthenticatedWarning />}
                  {emailTaken && <FacebookEmailTakenWarning />}
                  {form}
                </MarginGroup>
              </AuthPageContent>
            </Page>
          );
        }
      }}</LoginConsumer>
    </LoginProvider>
  );
};

export default Auth;
